import { useCallback } from 'react';
import { useSocketEvent } from '@connect-x-app/hybrid-data-access';
import {
  Notification,
  NotificationDismissedEvent,
  NotificationEvent,
  NotificationHiddenEvent,
  NotificationLink,
  NotificationStatus,
  NotificationType,
} from '@connect-x-app/shared-domain-models';

import {
  useAuthenticationStore,
  useChannelSessionStore,
  useFlightRoomSessionStore,
  useFlightRoomStore,
  useNotificationStore,
  useSocketStore,
} from '../../stores';
import { useNotificationSocketSendEvents } from './useNotificationSocketSendEvents';

export const useNotificationSocketEvents = () => {
  const { socket } = useSocketStore();
  const { addNotification, markNotificationAsDismissed, removeNotificationById } = useNotificationStore();
  const { user } = useAuthenticationStore();
  const { selectedFlightRoomId } = useFlightRoomSessionStore();
  const { flightRooms, updatePartialFlightRoom } = useFlightRoomStore();
  const { selectedChannelId } = useChannelSessionStore();
  const { dismissNotification } = useNotificationSocketSendEvents();

  const handleNotificationSent = useCallback(
    (event: NotificationEvent) => {
      const isMention = event.type === NotificationType.MENTION;
      const isNotificationInOpenChat = selectedFlightRoomId === event.chatroomId && selectedChannelId === event.channelId;

      const link: NotificationLink = {
        roomId: event.chatroomId,
        ...(isMention && {
          channelId: event.channelId,
          channelType: event.channelType,
          messageId: event.messageId,
        }),
      };

      const notification: Notification = {
        body: event.body,
        createdAt: event.createdAt,
        dismissedAt: null,
        id: event.notificationId,
        link,
        status: isNotificationInOpenChat ? NotificationStatus.DISMISSED : NotificationStatus.SENT,
        title: event.title,
        type: event.type,
      };

      addNotification(notification);

      const room = Array.from(flightRooms.values()).find(f => f.id === event.chatroomId);

      if (user && room && isMention && room.id !== selectedFlightRoomId) {
        updatePartialFlightRoom(room.id, { hasUnreadMentions: true });
      }

      if (isNotificationInOpenChat) {
        dismissNotification(notification.id);
      }
    },
    [addNotification, dismissNotification, flightRooms, selectedChannelId, selectedFlightRoomId, updatePartialFlightRoom, user],
  );

  const handleNotificationDismissed = useCallback(
    ({ notificationId }: NotificationDismissedEvent) => {
      markNotificationAsDismissed(notificationId);
    },
    [markNotificationAsDismissed],
  );

  const handleNotificationHidden = useCallback(
    ({ notificationId }: NotificationHiddenEvent) => {
      removeNotificationById(notificationId);
    },
    [removeNotificationById],
  );

  useSocketEvent(socket, 'notifications.notification-sent', handleNotificationSent);
  useSocketEvent(socket, 'notifications.notification-dismissed', handleNotificationDismissed);
  useSocketEvent(socket, 'notifications.notification-hidden', handleNotificationHidden);
};
